import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiCheck, FiArrowRight, FiUsers, FiShield, 
  FiLayout, FiCode, FiHeadphones, FiServer, FiShare2, FiActivity,
  FiSearch, FiTarget, FiRefreshCw, FiLayers, FiBriefcase, FiZap, FiMail
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SampleNote from '../components/SampleNote';
import { sampleAgencyNote } from '../data/examples';
import '../styles/home.css';

const AgencyServices = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Agency Services | ManagedSite.net";
    
    const description = "ManagedSite.net provides white-label website management, technical support, agency hosting, email support, project delivery and technology consulting for agencies.";
    const title = "Agency Services | ManagedSite.net";
    
    const updateMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    
    updateMeta('meta[name="description"]', 'content', description);
    updateMeta('meta[property="og:title"]', 'content', title);
    updateMeta('meta[property="og:description"]', 'content', description);
    updateMeta('meta[property="twitter:title"]', 'content', title);
    updateMeta('meta[property="twitter:description"]', 'content', description);
  }, []);

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative bg-[#FAF8F3] pt-24 pb-24 lg:pt-40 lg:pb-36 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">Agency Services</div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#17211E] tracking-tight leading-[1.1] mb-10 max-w-4xl mx-auto">
            A Technical Team Behind Your Brand
          </h1>
          <p className="text-xl sm:text-2xl text-[#17211E]/80 leading-relaxed mb-12 max-w-3xl mx-auto font-medium">
            ManagedSite.net helps agencies deliver website management, technical support, hosting, email support and project work without hiring a full internal technical bench.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <Link to="/agency-plans" className="home-button">
              View Agency Plans
            </Link>
            <Link to="/contact" className="home-button-secondary">
              Discuss an Agency Partnership
            </Link>
          </div>
          <p className="text-lg text-[#17211E]/50 font-bold italic">
            Your agency owns the client relationship. ManagedSite.net works behind the scenes unless otherwise agreed.
          </p>
        </div>
      </section>

      {/* Why Agencies Use Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">Expand Delivery Without Expanding Headcount</h2>
            <p className="text-xl text-[#17211E]/70 leading-relaxed font-medium">
              Agencies need dependable technical capacity for client websites and digital systems. ManagedSite.net provides that capacity under your brand.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: "White-Label Delivery", text: "Support client websites without giving up the client relationship.", icon: FiUsers },
              { title: "Predictable Operations", text: "Maintenance, monitoring, backups and support can run on clear service levels.", icon: FiActivity },
              { title: "Overflow Capacity", text: "Get help with projects, migrations, troubleshooting and urgent issues when your team is full.", icon: FiZap },
              { title: "One Technical Partner", text: "Reduce the gap between website, hosting, email, security and support vendors.", icon: FiLayers }
            ].map((item, i) => (
              <div key={i} className="bg-[#FAF8F3] p-10 rounded-3xl border border-[#EAF1EC] flex flex-col h-full">
                <div className="text-[#174C3C] mb-8">
                  <SafeIcon icon={item.icon} className="w-10 h-10" />
                </div>
                <h3 className="text-2xl font-bold text-[#17211E] mb-4">{item.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-16 lg:grid-cols-[1fr_1.05fr]">
            <div>
              <div className="mb-5 text-xs font-bold uppercase tracking-[0.2em] text-[#3B7C6D]">
                What you can forward
              </div>
              <h2 className="text-4xl font-bold text-[#17211E] leading-tight md:text-5xl">
                A note in your brand, not ours.
              </h2>
              <p className="mt-7 max-w-xl text-xl leading-relaxed text-[#17211E]/70 font-medium">
                Eligible plans include a written note you can send as your own: what was reviewed, what changed, and what needs a client decision. We stay off the page.
              </p>
              <Link to="/agency-plans" className="home-link mt-8 inline-block">
                See agency plans
              </Link>
            </div>
            <SampleNote note={sampleAgencyNote} />
          </div>
        </div>
      </section>

      {/* Core Agency Services */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">What We Provide for Agencies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              { title: "White-Label Website Management", text: "Ongoing updates, security, backups, monitoring and reporting.", link: "/agency-services/white-label-wordpress", icon: FiLayout },
              { title: "White-Label Technical Support", text: "Troubleshooting, infrastructure help and prepaid support blocks.", link: "/agency-services/white-label-support", icon: FiHeadphones },
              { title: "Agency Hosting", text: "Hosting managed by the same team that manages the website.", link: "/agency-services/agency-hosting", icon: FiServer },
              { title: "Business Email Support", text: "Microsoft 365, Google Workspace, migrations and Email Reliability.", link: "/services/business-email", icon: FiMail },
              { title: "Web Design and Development", text: "Overflow support for builds, redesigns, landing pages and migrations.", link: "/services/web-design-and-development", icon: FiCode },
              { title: "Technology Consulting", text: "Planning, vendor evaluation, platform selection and digital operations guidance.", link: "/services/technology-consulting", icon: FiShare2 }
            ].map((service, i) => (
              <div key={i} className="bg-white p-10 rounded-[2.5rem] border border-[#EAF1EC] shadow-sm flex flex-col h-full">
                <div className="bg-[#EAF1EC] w-14 h-14 rounded-2xl flex items-center justify-center text-[#174C3C] mb-8">
                  <SafeIcon icon={service.icon} className="w-7 h-7" />
                </div>
                <h3 className="text-2xl font-extrabold text-[#17211E] mb-6">{service.title}</h3>
                <p className="text-lg text-[#17211E]/70 leading-relaxed font-medium mb-10 flex-grow">{service.text}</p>
                <Link to={service.link} className="text-[#174C3C] font-bold hover:text-[#3B7C6D] flex items-center gap-2 text-lg">
                  Learn More <SafeIcon icon={FiArrowRight} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Agency Plans Highlight */}
      <section className="py-24 bg-[#EAF1EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-[#17211E] mb-6">White-Label Website Care Plans</h2>
            <p className="text-xl text-[#17211E]/75 font-medium">Ongoing website management, starting at $59 per site each month. We don't publish the full rate card here — partner rates stay private so you can set your own client pricing and protect your margin.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              "Essential Care",
              "Managed Care",
              "Advanced Care"
            ].map((plan) => (
              <div key={plan} className="bg-white p-10 rounded-3xl border border-[#3B7C6D]/10 text-center shadow-sm">
                <h3 className="text-xl font-bold text-[#17211E] mb-4">{plan}</h3>
                <div className="text-sm font-bold text-[#17211E]/40 uppercase tracking-widest">Per Site</div>
              </div>
            ))}
          </div>
          <div className="max-w-4xl mx-auto bg-white/50 border border-[#3B7C6D]/10 p-10 rounded-[2rem] text-center">
            <div className="text-2xl font-extrabold text-[#17211E] mb-4">Volume rates start at 10 active sites</div>
            <p className="text-lg text-[#17211E]/70 font-medium mb-10">
              Once an agency reaches 10 active eligible websites, a volume rate applies to all eligible websites beginning with the next billing cycle.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="home-button">
                Request Partner Pricing
              </Link>
              <Link to="/agency-plans" className="home-button-secondary">
                Compare Care Plans
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How Partnership Works */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-20 text-center">How Agency Partnership Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {[
              { title: "Review", text: "We review your client-website needs, current tools and support gaps." },
              { title: "Set the Model", text: "Choose managed plans, support blocks, hosting, project help or a mixed model." },
              { title: "Onboard", text: "We connect monitoring, maintenance and communication workflows behind your brand." },
              { title: "Deliver and Improve", text: "We manage the technical work, report clearly and help your agency scale delivery." }
            ].map((step, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-black text-[#EAF1EC] mb-8">{i + 1}</div>
                <h3 className="text-2xl font-bold text-[#17211E] mb-5">{step.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support and Project Help */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Support When Clients Need Answers Quickly</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              { title: "Included Plan Support", text: "Some care plans include limited technical support time." },
              { title: "Prepaid Support Blocks", text: "5-hour and 10-hour blocks. Partner rates are on the rate card." },
              { title: "On-Demand Agency Support", text: "Hourly white-label technical work outside included time. The rate is on the partner rate card." }
            ].map((item, i) => (
              <div key={i} className="bg-white p-10 rounded-3xl border border-[#EAF1EC] shadow-sm">
                <h3 className="text-xl font-extrabold text-[#17211E] mb-6">{item.title}</h3>
                <p className="text-lg text-[#17211E]/70 font-medium leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-lg text-[#17211E]/50 font-bold italic mb-10">
              Emergency or after-hours availability is not guaranteed. When available, urgent work is billed at the emergency partner rate.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/contact" className="home-button">
                Request Support
              </Link>
              <Link to="/agency-services/white-label-support" className="home-button-secondary">
                View White-Label Support
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Built for Agencies and Service Providers</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-16">
            {[
              "Web design and marketing agencies",
              "Freelancers and consultants managing client websites",
              "IT and service providers that need WordPress and digital operations help"
            ].map((item, i) => (
              <div key={i} className="bg-[#FAF8F3] p-12 rounded-[2.5rem] border border-[#EAF1EC] shadow-sm text-center">
                <p className="text-xl font-extrabold text-[#17211E] leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <p className="text-xl text-[#17211E]/75 font-medium mb-8">
              Need help for your own business instead?
            </p>
            <Link to="/contact" className="inline-flex items-center text-[#174C3C] font-extrabold hover:text-[#3B7C6D] transition-colors text-xl gap-3">
              Tell us what you need help with <SafeIcon icon={FiArrowRight} />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 bg-[#174C3C] text-center relative overflow-hidden text-white">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-10 tracking-tight leading-tight">
            Give Your Agency a Dependable Technical Backbone
          </h2>
          <p className="text-2xl text-[#FAF8F3]/80 mb-14 max-w-3xl mx-auto leading-relaxed font-medium">
            White-label website management, support, hosting and project delivery from one technical partner.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-14">
            <Link to="/agency-plans" className="home-button-on-dark">
              View Agency Plans
            </Link>
            <Link to="/contact" className="home-button-on-dark-secondary">
              Discuss an Agency Partnership
            </Link>
          </div>
          <p className="text-[#FAF8F3]/60 text-lg font-medium">
            Need help for your own business? <Link to="/digital-review" className="text-[#D7B77A] hover:underline font-bold">Tell us what you need help with.</Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default AgencyServices;